/**
 * Reverse-DNS identifier carried in ADF `extension` nodes for a Hub
 * Connect extension. Must remain stable across product releases — changing
 * it is a breaking change to persisted hub content.
 *
 * Mirrors the Help Center Connect module shape so installed Connect apps
 * (Portal header / Portal footer modules) can be shared between Help
 * Center and Hub editors with zero schema translation.
 */
export const HUB_CONNECT_EXTENSION_TYPE = 'com.atlassian.hub-connect';

/**
 * Logical key used by `getExtensionManifest` and by the registry in
 * `@atlassian/hub-extension-schemas`. Kept in sync with
 * `HUB_CONNECT_EXTENSION_TYPE` minus the reverse-DNS prefix.
 */
export const HUB_CONNECT_EXTENSION_KEY = 'hub-connect';

/**
 * Per-slot manifest keys emitted into ADF `extensionKey`. Mirrors the manifest
 * entries in `ui/hub-connect-manifest`.
 */
export const HUB_CONNECT_HEADER_EXTENSION_KEY: string = `${HUB_CONNECT_EXTENSION_KEY}-header`;
export const HUB_CONNECT_FOOTER_EXTENSION_KEY: string = `${HUB_CONNECT_EXTENSION_KEY}-footer`;

export const isHeaderExtensionKey = (extensionKey: string | undefined): boolean =>
	extensionKey === HUB_CONNECT_HEADER_EXTENSION_KEY;

export const isFooterExtensionKey = (extensionKey: string | undefined): boolean =>
	extensionKey === HUB_CONNECT_FOOTER_EXTENSION_KEY;

/**
 * Default semver carried in ADF `parameters.version`. Bump when the Zod
 * schema changes shape in a non-backwards-compatible way.
 */
export const HUB_CONNECT_EXTENSION_VERSION_1 = '1.0.0';

/**
 * Identifies which slot of the hub a Connect extension was inserted into.
 * Drives the insert-menu manifest (one entry per slot) and the labelled
 * chip shown above the extension node in the editor.
 */
export const ConnectElementType = {
	HEADER_AND_SUB_HEADER_PANELS: 'headerAndSubheaderPanels',
	FOOTER_PANELS: 'footerPanels',
} as const;

export type ConnectElementType = (typeof ConnectElementType)[keyof typeof ConnectElementType];

/**
 * Page context the extension renders inside. Currently scoped to Help Hub.
 */
export const ConnectPageContext = {
	HELP_HUB: 'HELP_HUB',
} as const;

export type ConnectPageContext = (typeof ConnectPageContext)[keyof typeof ConnectPageContext];

export const HUB_CONNECT_EXTENSION_ANALYTIC_SOURCE = 'hubConnectExtension';
export const HUB_CONNECT_EXTENSION_CONFIG_ANALYTIC_SOURCE = 'hubConnectExtensionConfig';

/**
 * Default URL surfaced as "Learn more" in the config-panel module card when
 * the consumer does not supply a more specific link. Points at the Forge
 * Jira Service Management module manifest reference.
 */
export const HUB_CONNECT_LEARN_MORE_URL =
	'https://developer.atlassian.com/platform/forge/manifest-reference/modules/index-jsm/';
